import React, { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';

const medals = { 1: '🥇', 2: '🥈', 3: '🥉' };

function SortBtn({ col, label, sort, setSort, align }) {
  const active = sort.col === col;
  const dir = active ? sort.dir : null;
  const next = !active ? 'asc' : dir === 'asc' ? 'desc' : null;
  return (
    <button onClick={() => setSort({ col: next ? col : null, dir: next })}
      style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
        display: 'flex', alignItems: 'center', gap: 4,
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        color: active ? 'var(--blue)' : 'inherit',
        fontFamily: 'var(--font-condensed)', fontWeight: 700,
        fontSize: '0.85rem', letterSpacing: '1px', textTransform: 'uppercase',
        width: '100%',
      }}>
      {label}
      <span style={{ fontSize: '0.7rem', opacity: active ? 1 : 0.4 }}>
        {dir === 'asc' ? '↑' : dir === 'desc' ? '↓' : '↕'}
      </span>
    </button>
  );
}

export default function LeaderboardPage() {
  const api = useApi();
  const { currentUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [sort, setSort] = useState({ col: null, dir: null }); // null = default (by rank)
  const [showSleeper, setShowSleeper] = useState(false);
  const [showTiebreaker, setShowTiebreaker] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_URL || '/api';

    async function loadLeaderboard() {
      try {
        const result = await api.getLeaderboard();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    }

    // Initial load
    loadLeaderboard();

    // Poll /scores/updated every 15s — always checks for score changes
    // (covers both ESPN poller updates AND manual admin recalculations)
    // Separately check poller/active to adjust poll frequency:
    //   - Active window: every 15s (games in progress)
    //   - Inactive window: every 60s (nothing happening, save resources)
    let currentInterval = 15000;
    let pollTimeout;

    const checkForUpdates = async () => {
      try {
        // Always check for score changes regardless of poller state
        const [activeRes, updatedRes] = await Promise.all([
          fetch(`${API_BASE}/poller/active`),
          fetch(`${API_BASE}/scores/updated`),
        ]);
        const { active } = await activeRes.json();
        const { lastScoreUpdate } = await updatedRes.json();

        // Reload leaderboard if scores changed
        if (lastScoreUpdate && lastScoreUpdate !== lastUpdate) {
          setLastUpdate(lastScoreUpdate);
          setRefreshing(true);
          loadLeaderboard();
        }

        // Adjust next poll interval based on whether poller is active
        const nextInterval = active ? 15000 : 600000; // 15s active, 10min inactive
        pollTimeout = setTimeout(checkForUpdates, nextInterval);
      } catch (_) {
        pollTimeout = setTimeout(checkForUpdates, 60000);
      }
    };

    pollTimeout = setTimeout(checkForUpdates, 15000);
    const pollInterval = { current: pollTimeout }; // for cleanup

    return () => clearTimeout(pollTimeout);
  }, [lastUpdate]); // re-register when lastUpdate changes

  if (loading) return (
    <div className="loading-container">
      <div className="spinner"></div>
      <div className="loading-text">Loading leaderboard...</div>
    </div>
  );

  const filtered = (data?.entries || []).filter(e =>
    !search ||
    e.userName?.toLowerCase().includes(search.toLowerCase()) ||
    e.bracketName?.toLowerCase().includes(search.toLowerCase())
  );

  const entries = [...filtered].sort((a, b) => {
    if (!sort.col || !sort.dir) return 0; // default: keep rank order
    const d = sort.dir === 'asc' ? 1 : -1;
    switch (sort.col) {
      case 'rank':    return d * (a.rank - b.rank);
      case 'bracket': return d * (a.bracketName || '').localeCompare(b.bracketName || '');
      case 'name':    return d * (a.userName || '').localeCompare(b.userName || '');
      case 'score':   return d * (a.score - b.score);
      case 'sleeper': return d * ((a.sleeperPts || 0) - (b.sleeperPts || 0));
      case 'tiebreaker': return d * ((a.tiebreaker ?? 999) - (b.tiebreaker ?? 999));
      default: return 0;
    }
  });

  const myEntries = data?.entries?.filter(e => e.userId === currentUser?.uid) || [];
  const myBest = myEntries[0];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">LEADER<span>BOARD</span></h1>
        <p className="page-subtitle">
          {data?.total} brackets competing · Last updated {data?.lastUpdated ? new Date(data.lastUpdated).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
          {refreshing && <span style={{ marginLeft: 10, color: 'var(--blue)', fontSize: '0.8rem' }}>↻ updating...</span>}
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {myBest && (
        <div className="alert alert-info" style={{ marginBottom: 24 }}>
          Your best bracket "<strong>{myBest.bracketName}</strong>" is ranked
          <strong> #{myBest.rank}</strong> with <strong>{myBest.score} pts</strong>
          {myEntries.length > 1 && ` (${myEntries.length} brackets total)`}
        </div>
      )}

      {/* Top 3 Podium */}
      {filtered.filter(e => e.rank <= 3).length >= 3 && !search && (
        <div className="grid-3" style={{ marginBottom: 28 }}>
          {[2, 1, 3].map(rank => {
            const entry = filtered.find(e => e.rank === rank);
            if (!entry) return null;
            return (
              <div key={entry.bracketId} className="stat-box"
                style={{
                  border: rank === 1 ? '2px solid var(--gold)' : '1px solid var(--slate)',
                  transform: rank === 1 ? 'scale(1.04)' : 'scale(1)',
                }}>
                <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>{medals[rank]}</div>
                <div className="stat-value">{entry.score}</div>
                <div className="stat-label">{entry.bracketName}</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--gray)', marginTop: 4 }}>{entry.userName}</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">ALL BRACKETS</h2>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {sort.col && (
              <button className="btn btn-secondary btn-sm" onClick={() => setSort({ col: null, dir: null })}>
                Reset Sort
              </button>
            )}
            <button
              onClick={() => {
                setShowSleeper(s => !s);
                if (sort.col === 'sleeper') setSort({ col: null, dir: null });
              }}
              style={{
                padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
                fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '0.85rem',
                letterSpacing: '0.5px', border: '1px solid',
                borderColor: showSleeper ? 'var(--gold)' : 'var(--slate)',
                background: showSleeper ? 'rgba(241,196,15,0.12)' : 'transparent',
                color: showSleeper ? 'var(--gold)' : 'var(--gray)',
              }}>
              ★ {showSleeper ? 'Hide' : 'Show'} Sleeper Pts
            </button>
            <button
              onClick={() => {
                setShowTiebreaker(s => !s);
                if (sort.col === 'tiebreaker') setSort({ col: null, dir: null });
              }}
              style={{
                padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
                fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '0.85rem',
                letterSpacing: '0.5px', border: '1px solid',
                borderColor: showTiebreaker ? 'var(--blue)' : 'var(--slate)',
                background: showTiebreaker ? 'rgba(75,139,190,0.12)' : 'transparent',
                color: showTiebreaker ? 'var(--blue)' : 'var(--gray)',
              }}>
              🎯 {showTiebreaker ? 'Hide' : 'Show'} Tiebreaker
            </button>
            <input className="form-input" type="text" placeholder="Search..."
              value={search} onChange={e => setSearch(e.target.value)} style={{ width: 200 }} />
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th style={{ width: 60 }}>
                  <SortBtn col="rank" label="Rank" sort={sort} setSort={setSort} />
                </th>
                <th><SortBtn col="bracket" label="Bracket" sort={sort} setSort={setSort} /></th>
                <th><SortBtn col="name" label="Name" sort={sort} setSort={setSort} /></th>
                {showSleeper && (
                  <th style={{ textAlign: 'right' }}>
                    <SortBtn col="sleeper" label="★ Sleeper" sort={sort} setSort={setSort} align="right" />
                  </th>
                )}
                {showTiebreaker && (
                  <th style={{ textAlign: 'right' }}>
                    <SortBtn col="tiebreaker" label="🎯 TB" sort={sort} setSort={setSort} align="right" />
                  </th>
                )}
                <th style={{ textAlign: 'right' }}>
                  <SortBtn col="score" label="Score" sort={sort} setSort={setSort} align="right" />
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => {
                const isMe = entry.userId === currentUser?.uid;
                return (
                  <tr key={entry.bracketId} className={`rank-${entry.rank}`}
                    style={isMe ? { background: 'rgba(255,107,0,0.08)' } : {}}>
                    <td>
                      <span className="rank-number">{medals[entry.rank] || `#${entry.rank}`}</span>
                    </td>
                    <td style={{ fontWeight: isMe ? 700 : 600 }}>
                      {entry.bracketName}
                      {isMe && <span className="badge badge-orange" style={{ marginLeft: 8 }}>You</span>}
                    </td>
                    <td style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>{entry.userName}</td>
                    {showSleeper && (
                      <td style={{ textAlign: 'right' }}>
                        <span style={{
                          fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1.1rem',
                          color: entry.sleeperPts > 0 ? 'var(--gold)' : 'var(--gray)'
                        }}>
                          {entry.sleeperPts > 0 ? `★ ${entry.sleeperPts}` : '—'}
                        </span>
                      </td>
                    )}
                    {showTiebreaker && (
                      <td style={{ textAlign: 'right' }}>
                        <span style={{
                          fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1.1rem',
                          color: 'var(--blue)'
                        }}>
                          {entry.tiebreaker != null ? entry.tiebreaker : '—'}
                        </span>
                      </td>
                    )}
                    <td style={{ textAlign: 'right' }}>
                      <span className="score-value">{entry.score}</span>
                    </td>
                  </tr>
                );
              })}
              {entries.length === 0 && (
                <tr>
                  <td colSpan={(showSleeper ? 1 : 0) + (showTiebreaker ? 1 : 0) + 4} style={{ textAlign: 'center', color: 'var(--gray)', padding: '32px' }}>
                    No entries found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
